import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ROUTES } from "../../../../app/config/routes";
import { useTheme } from "../../../../app/providers/ThemeProvider";
import "remixicon/fonts/remixicon.css";
import "./Navbar.css";
import { motion, AnimatePresence } from "framer-motion";

//logos
import logodark from "../../../../assets/images/Tencotics,logo-02.png";
import logolight from "../../../../assets/images/Tencotics,logo-04.png";

const navLinks = [
  { to: ROUTES.QUIENES_SOMOS, icon: "ri-user-3-line", label: "Nosotros" },
  {
    to: ROUTES.SERVICIOS_TI,
    icon: "ri-settings-3-line",
    label: "Servicios TI",
  },
  {
    to: ROUTES.SERVICIOS_CLOUD,
    icon: "ri-cloud-line",
    label: "Servicios Cloud",
  },
  {
    to: ROUTES.INFRAESTRUCTURA,
    icon: "ri-server-line",
    label: "Infraestructura",
  },
  { 
    to: ROUTES.DESARROLLO, 
    icon: "ri-code-s-slash-line", 
    label: "Desarrollo" 
  },
  {
    to: ROUTES.PROYECTOS,
    icon: "ri-code-s-slash-line",
    label: "Nuestros Proyectos",
  },
  {
    to: ROUTES.CONTACTO,
    icon: "ri-customer-service-2-line",
    label: "Contacto",
  },
  { to: ROUTES.LOGIN_EXTERNO, icon: "ri-user-3-line", label: "Login", isLogin: true },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <motion.nav
      className={`navbar ${scrolled ? "scrolled" : ""}`}
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="navbar-container">
        <motion.div
          className="navbar-brand"
          initial={{ opacity: 0, x: -32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            delay: 0.2,
            duration: 0.7,
            type: "spring",
            stiffness: 80,
          }}
        >
          <Link to={ROUTES.HOME} onClick={() => setIsOpen(false)}>
            <motion.img
              src={theme === "dark" ? logodark : logolight}
              alt="Tecnotics Logo"
              className="brand-logo"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.4,
                duration: 0.6,
                type: "spring",
                stiffness: 120,
              }}
              whileHover={{ scale: 1.08, rotate: -3 }}
            />
          </Link>
        </motion.div>

        <div className="navbar-actions">
          <motion.button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label="Cambiar tema"
            whileHover={{ scale: 1.15, rotate: 15 }}
            whileTap={{ scale: 0.95, rotate: 0 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            {theme === "dark" ? (
              <i className="ri-sun-line"></i>
            ) : (
              <i className="ri-moon-line"></i>
            )}
          </motion.button>
          <motion.button
            className={`hamburger ${isOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-label="Menú"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300 }}
            style={{ position: "relative", zIndex: 3000 }}
          >
            {isOpen ? (
              <motion.i
                key="close"
                className="ri-close-line"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                style={{ fontSize: "1.5rem", color: "var(--accent)" }}
              />
            ) : (
              <>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </>
            )}
          </motion.button>
        </div>

        {/* Desktop nav */}
        <div className="nav-menu">
          <ul className="nav-links">
            {navLinks.map((link) => (
              <motion.li
                key={link.to}
                whileHover={{ scale: 1.08, y: -2 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Link
                  to={link.to}
                  className={link.isLogin ? "profile-login-btn" : undefined}
                >
                  <motion.i
                    className={`${link.icon} nav-icon`}
                    whileHover={{
                      color: "var(--accent)",
                      scale: 1.2,
                      rotate: -8,
                    }}
                    transition={{ type: "spring", stiffness: 300 }}
                  />
                  <span>{link.label}</span>
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Mobile nav */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="nav-menu active"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                height: "100vh",
                zIndex: 2000,
              }}
            >
              <ul className="nav-links">
                {navLinks.map((link) => (
                  <motion.li
                    key={link.to}
                    whileHover={{ scale: 1.08, x: 8 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Link
                      to={link.to}
                      onClick={() => setIsOpen(false)}
                      className={link.isLogin ? "profile-login-btn" : undefined}
                    >
                      <motion.i
                        className={`${link.icon} nav-icon`}
                        whileHover={{
                          color: "var(--accent)",
                          scale: 1.2,
                          rotate: -8,
                        }}
                        transition={{ type: "spring", stiffness: 300 }}
                      />
                      <span>{link.label}</span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
